// The position that accompanied an SOS/fall alarm (documentation/protocol.md "SOS"). Radios attach
// a position only if its GPS fix is at most 30 s old, with the fix's own time; older apps send a
// position without one. 0/0 or a missing position means no fresh position came with the alarm.
import type { SosEvent } from '../services/admin';

export type AlarmPosition =
  | { kind: 'none' }
  | { kind: 'fix'; latitude: number; longitude: number; accuracyM: number | null; fixAt: Date; secondsBeforeAlarm: number }
  | { kind: 'unknown-age'; latitude: number; longitude: number; accuracyM: number | null };

type PositionFields = Pick<SosEvent, 'latitude' | 'longitude' | 'location_timestamp' | 'location_accuracy_m' | 'created_at'>;

export function alarmPosition(e: PositionFields): AlarmPosition {
  const { latitude, longitude } = e;
  if (latitude == null || longitude == null || (latitude === 0 && longitude === 0)) return { kind: 'none' };
  const accuracyM = e.location_accuracy_m ?? null;
  if (!e.location_timestamp) return { kind: 'unknown-age', latitude, longitude, accuracyM };
  const fixAt = new Date(e.location_timestamp);
  const secondsBeforeAlarm = Math.max(0, Math.round((new Date(e.created_at).getTime() - fixAt.getTime()) / 1000));
  return { kind: 'fix', latitude, longitude, accuracyM, fixAt, secondsBeforeAlarm };
}

const coords = (lat: number, lon: number) => `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
const accuracy = (m: number | null) => (m == null ? '' : ` ±${Math.round(m)} m`);

/** English, for admin pages. */
export function describeAlarmPosition(e: PositionFields): string {
  const p = alarmPosition(e);
  switch (p.kind) {
    case 'none':
      return 'No fresh GPS position with the alarm';
    case 'unknown-age':
      return `${coords(p.latitude, p.longitude)}${accuracy(p.accuracyM)} · GPS fix time unknown (older app)`;
    case 'fix':
      return `${coords(p.latitude, p.longitude)}${accuracy(p.accuracyM)} · GPS fix ${p.fixAt.toLocaleTimeString()} (${p.secondsBeforeAlarm} s before the alarm)`;
  }
}

/** Danish, for the dispatcher alarm banner. */
export function describeAlarmPositionDa(e: PositionFields): string {
  const p = alarmPosition(e);
  switch (p.kind) {
    case 'none':
      return 'ingen frisk GPS-position';
    case 'unknown-age':
      return `GPS ${coords(p.latitude, p.longitude)}${accuracy(p.accuracyM)} (fix-tid ukendt)`;
    case 'fix':
      return `GPS ${coords(p.latitude, p.longitude)}${accuracy(p.accuracyM)} (fix kl. ${p.fixAt.toLocaleTimeString()}, ${p.secondsBeforeAlarm} s før alarm)`;
  }
}
